/**
 * Sample event templates for Developer Console
 * Based on EventSchema from fh_kafka/schema-docs/event/event-schema.md
 */

import type { EventTemplateData, CustomEvent } from '../types/ingestion.types'

/**
 * Generate current timestamp in ISO format
 */
export const getCurrentTimestamp = (): string => new Date().toISOString()

/**
 * Generate unique event ID
 */
const generateEventId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2)
}

/**
 * Generate unique FHID
 */
const generateFhid = (): string => {
  return 'fhid_' + Date.now().toString(36) + Math.random().toString(36).substring(2)
}

/**
 * Sample event templates following the correct EventSchema structure
 */
export const EVENT_TEMPLATES: Record<string, EventTemplateData> = {
  page_view: {
    name: 'Page View',
    description: 'Website page view tracking event',
    category: 'user_action',
    event_name: 'page_view',
    template: {
      acc_id: 12345,
      category: 'user_action',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'page_view',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        brand: 'Apple',
        browser: 'Safari',
        os: 'iOS',
        type: 'mobile',
      },
      custom: {
        s1: 'Homepage visit',
        s2: '/products',
        n1: 1.0,
      },
    },
  },

  purchase: {
    name: 'Purchase',
    description: 'E-commerce purchase transaction event',
    category: 'transaction',
    event_name: 'purchase',
    template: {
      acc_id: 12345,
      category: 'transaction',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'purchase',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Chrome',
        os: 'Windows',
        type: 'desktop',
      },
      custom: {
        s1: 'ORD-123456', // order_id
        s2: 'USD', // currency
        n1: 199.99, // amount
        n2: 2, // quantity
        s3: 'PROD-001', // product_id
        s4: 'credit_card', // payment_method
      },
    },
  },

  add_to_cart: {
    name: 'Add to Cart',
    description: 'Product added to shopping cart',
    category: 'user_action',
    event_name: 'add_to_cart',
    template: {
      acc_id: 12345,
      category: 'user_action',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'add_to_cart',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Firefox',
        os: 'Android',
        type: 'mobile',
      },
      custom: {
        s1: 'PROD-002', // product_id
        s2: 'Product Name', // product_name
        n1: 49.99, // price
        n2: 1, // quantity
      },
    },
  },

  signup: {
    name: 'User Signup',
    description: 'New user registration event',
    category: 'user_lifecycle',
    event_name: 'signup',
    template: {
      acc_id: 12345,
      category: 'user_lifecycle',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'signup',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Chrome',
        os: 'macOS',
        type: 'desktop',
      },
      custom: {
        s1: 'email', // signup_method
        s2: 'organic', // source
        s3: 'free', // plan_type
      },
    },
  },

  login: {
    name: 'User Login',
    description: 'User login event',
    category: 'user_action',
    event_name: 'login',
    template: {
      acc_id: 12345,
      category: 'user_action',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'login',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        brand: 'Samsung',
        browser: 'Chrome',
        os: 'Android',
        type: 'mobile',
      },
      custom: {
        s1: 'password', // login_method
        b1: true, // success
      },
    },
  },

  checkout: {
    name: 'Checkout Started',
    description: 'User initiated checkout process',
    category: 'transaction',
    event_name: 'checkout',
    template: {
      acc_id: 12345,
      category: 'transaction',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'checkout',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Safari',
        os: 'iOS',
        type: 'mobile',
      },
      custom: {
        n1: 299.99, // cart_total
        n2: 3, // item_count
        s1: 'USD', // currency
      },
    },
  },

  profile_update: {
    name: 'Profile Update',
    description: 'User profile information updated',
    category: 'user_action',
    event_name: 'profile_update',
    template: {
      acc_id: 12345,
      category: 'user_action',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'profile_update',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Chrome',
        os: 'Windows',
        type: 'desktop',
      },
      custom: {
        s1: 'email', // field_updated
        s2: 'settings_page', // update_source
      },
    },
  },

  search: {
    name: 'Search',
    description: 'User performed a search',
    category: 'user_action',
    event_name: 'search',
    template: {
      acc_id: 12345,
      category: 'user_action',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'search',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Chrome',
        os: 'macOS',
        type: 'desktop',
      },
      custom: {
        s1: 'laptop computers', // search_query
        n1: 25, // results_count
        s2: 'products', // search_category
      },
    },
  },

  video_play: {
    name: 'Video Play',
    description: 'Video playback started',
    category: 'engagement',
    event_name: 'video_play',
    template: {
      acc_id: 12345,
      category: 'engagement',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'video_play',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Safari',
        os: 'iOS',
        type: 'mobile',
      },
      custom: {
        s1: 'VID-12345', // video_id
        s2: 'Product Demo', // video_title
        n1: 0, // play_position_seconds
        n2: 180, // video_duration_seconds
      },
    },
  },

  custom_event: {
    name: 'Custom Event',
    description: 'Generic custom event template',
    category: 'custom',
    event_name: 'custom_event',
    template: {
      acc_id: 12345,
      category: 'custom',
      event_date: getCurrentTimestamp(),
      event_id: generateEventId(),
      event_name: 'custom_event',
      event_time: getCurrentTimestamp(),
      event_type: 'custom',
      fhid: generateFhid(),
      device: {
        browser: 'Chrome',
        os: 'Windows',
        type: 'desktop',
      },
      custom: {
        s1: 'custom_value',
        n1: 1.0,
      },
    },
  },
}

/**
 * Get template by key and refresh timestamps
 */
export const getEventTemplate = (templateKey: string, accountId?: number): CustomEvent => {
  const template = EVENT_TEMPLATES[templateKey]?.template
  if (!template) {
    return EVENT_TEMPLATES.custom_event.template
  }

  const now = getCurrentTimestamp()
  return {
    ...template,
    acc_id: accountId || template.acc_id,
    event_date: now,
    event_time: now,
    event_id: generateEventId(),
    fhid: generateFhid(),
  }
}

/**
 * Get template name
 */
export const getTemplateName = (templateKey: string): string => {
  return EVENT_TEMPLATES[templateKey]?.name || templateKey
}

/**
 * Sample CSV data for bulk upload
 */
export const SAMPLE_CSV_EVENTS = `acc_id,category,event_name,event_type,event_time,event_date,fhid,event_id,custom_s1,custom_n1
12345,user_action,page_view,custom,2024-01-17T10:00:00Z,2024-01-17T10:00:00Z,fhid_001,evt_001,Homepage,1.0
12345,transaction,purchase,custom,2024-01-17T10:05:00Z,2024-01-17T10:05:00Z,fhid_002,evt_002,ORD-001,99.99
12345,user_lifecycle,signup,custom,2024-01-17T10:10:00Z,2024-01-17T10:10:00Z,fhid_003,evt_003,organic,0
12345,transaction,purchase,custom,2024-01-17T10:15:00Z,2024-01-17T10:15:00Z,fhid_004,evt_004,ORD-002,149.99
12345,user_action,login,custom,2024-01-17T10:20:00Z,2024-01-17T10:20:00Z,fhid_005,evt_005,password,0`

export const SAMPLE_CSV_CUSTOMERS = `customerId,email,firstName,lastName,phone,city,country
CUST-001,john.doe@example.com,John,Doe,+1234567890,New York,USA
CUST-002,jane.smith@example.com,Jane,Smith,+0987654321,Los Angeles,USA
CUST-003,bob.johnson@example.com,Bob,Johnson,+1122334455,Chicago,USA`

export const SAMPLE_CSV_TRANSACTIONS = `transactionId,customerId,timestamp,amount,currency,status,productId,quantity
TXN-001,user1@example.com,2024-01-17T10:00:00Z,99.99,USD,completed,PROD-001,2
TXN-002,user2@example.com,2024-01-17T10:05:00Z,149.99,USD,completed,PROD-002,1
TXN-003,user3@example.com,2024-01-17T10:10:00Z,79.99,USD,pending,PROD-003,3
TXN-004,user4@example.com,2024-01-17T10:15:00Z,199.99,USD,completed,PROD-001,1`

/**
 * Download sample CSV file
 */
export const downloadSampleCSV = (type: 'events' | 'customers' | 'transactions' = 'events') => {
  const content =
    type === 'events' ? SAMPLE_CSV_EVENTS :
    type === 'customers' ? SAMPLE_CSV_CUSTOMERS :
    SAMPLE_CSV_TRANSACTIONS
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', `sample_${type}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
